import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { FeatureFlagResponse } from '@/api/kubik/monolith'
import { checkRegisterFlag } from '@/api/kubik/monolith'
import { openModal } from '@/components/composed/Modal/actions'
import type { IAuthModalOptions } from '@/components/composed/modals/AuthModal/interfaces'
import { TIMEOUT_TO_GET_FEATURE_FLAG, USE_NEW_REGISTRATION_PAGE_FEATURE_FLAG } from '@/components/ui/AuthButton/constants'
import { logoutUser, resetAuthState } from '@/logic/auth/actions'
import { getProfilePath, isAuthenticated } from '@/logic/auth/reducer'
import type { RootState } from '@/redux/interfaces'
import { rejectAfterTimeout } from '@/utils/helpers'

const mapState = (state: RootState) => ({
  isAuthenticated: isAuthenticated()(state),
  profilePath: getProfilePath()(state),
})

export const useAuthenticateUser = () => {
  const dispatch = useDispatch()

  const handleOpenAuthModal = useCallback(
    async (contentOptions?: IAuthModalOptions, dialogId: string = 'AuthModal') => {
      if (!contentOptions?.codeAlreadySent) {
        dispatch(resetAuthState())
      }

      dispatch(
        openModal({
          options: { dialogId, shouldCloseOnMissingClick: false, onCloseModal: contentOptions?.onCloseModal },
          contentOptions,
        }),
      )
    },
    [dispatch],
  )

  return {
    ...useSelector(mapState),
    logoutUser: useCallback((redirectUrl = '/') => dispatch(logoutUser({ redirectUrl })), [dispatch]),
    openAuthModal: handleOpenAuthModal,
  }
}

export const isStartAuthModalAvailable = async (): Promise<boolean> => {
  try {
    const flag = (await Promise.race([
      rejectAfterTimeout(TIMEOUT_TO_GET_FEATURE_FLAG),
      checkRegisterFlag({
        flagKey: USE_NEW_REGISTRATION_PAGE_FEATURE_FLAG,
      }),
    ])) as FeatureFlagResponse

    return flag.useNewRegistration
  } catch {
    return false
  }
}
